import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { BookingCard } from "@/components/BookingCard";
import { AvailabilityManager } from "@/components/AvailabilityManager";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Loader2, Inbox, CalendarCheck, History, Clock } from "lucide-react";

export default function InstructorDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/bookings"); setBookings(data); }
    catch (e) {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const requests = bookings.filter((b) => b.status === "pending");
  const upcoming = bookings.filter((b) => b.status === "accepted");
  const history = bookings.filter((b) => ["completed", "denied", "cancelled"].includes(b.status));

  const stats = [
    { label: "New requests", value: requests.length, icon: Inbox, color: "text-amber-600 bg-amber-50" },
    { label: "Upcoming lessons", value: upcoming.length, icon: CalendarCheck, color: "text-emerald-600 bg-emerald-50" },
    { label: "Completed", value: bookings.filter((b) => b.status === "completed").length, icon: History, color: "text-sky-600 bg-sky-50" },
  ];

  const Empty = ({ text }) => (
    <div className="text-center py-16 text-muted-foreground" data-testid="empty-state">
      <Clock className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>{text}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-arctic-secondary tracking-tight" data-testid="dashboard-title">Instructor Hub</h1>
          <p className="text-muted-foreground mt-1">Welcome back, {user.name.split(" ")[0]}.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          {stats.map((s) => (
            <Card key={s.label} className="p-5 rounded-2xl border-border flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.color}`}><s.icon className="w-6 h-6" /></div>
              <div>
                <div className="font-heading font-extrabold text-2xl text-arctic-secondary">{s.value}</div>
                <div className="text-sm text-muted-foreground">{s.label}</div>
              </div>
            </Card>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-arctic-primary" /></div>
        ) : (
          <Tabs defaultValue="requests" className="mt-8">
            <TabsList data-testid="instructor-tabs">
              <TabsTrigger value="requests" data-testid="tab-requests">Requests ({requests.length})</TabsTrigger>
              <TabsTrigger value="upcoming" data-testid="tab-upcoming">Upcoming ({upcoming.length})</TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">History ({history.length})</TabsTrigger>
              <TabsTrigger value="availability" data-testid="tab-availability">Availability</TabsTrigger>
            </TabsList>
            <TabsContent value="requests" className="mt-6">
              {requests.length === 0 ? <Empty text="No pending requests right now." /> :
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{requests.map((b) => <BookingCard key={b.booking_id} booking={b} viewAs="instructor" onChange={load} />)}</div>}
            </TabsContent>
            <TabsContent value="upcoming" className="mt-6">
              {upcoming.length === 0 ? <Empty text="No upcoming lessons." /> :
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{upcoming.map((b) => <BookingCard key={b.booking_id} booking={b} viewAs="instructor" onChange={load} />)}</div>}
            </TabsContent>
            <TabsContent value="history" className="mt-6">
              {history.length === 0 ? <Empty text="No past lessons." /> :
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{history.map((b) => <BookingCard key={b.booking_id} booking={b} viewAs="instructor" onChange={load} />)}</div>}
            </TabsContent>
            <TabsContent value="availability" className="mt-6">
              <AvailabilityManager />
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}
